<script setup lang="ts">
const error = useError();

const statusCode = computed(() => error.value?.statusCode ?? 500);
const isServerError = computed(() => statusCode.value === 500);

const clearingCache = ref(false);

const goHome = () => {
  clearError({ redirect: "/" });
};

const clearClientCache = async () => {
  if (clearingCache.value) {
    return;
  }

  clearingCache.value = true;

  try {
    if (import.meta.client) {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }

      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((registration) => registration.unregister()),
        );
      }

      localStorage.clear();
      sessionStorage.clear();
    }
  } catch (err) {
    // This fallback can run when regular client-side error reporting is unavailable.
    // eslint-disable-next-line no-console
    console.error("Erro ao limpar o cache do navegador", err);
  } finally {
    if (import.meta.client) {
      clearError();
      window.location.reload();
    }
  }
};
</script>

<template>
  <v-app>
    <v-main class="error-page">
      <v-container class="error-container">
        <div class="error-card">
          <template v-if="isServerError">
            <div class="error-status">{{ statusCode }}</div>
            <h1 class="error-title">Ocorreu um erro</h1>
            <p class="error-description">
              Aconteceu um erro inesperado ao carregar esta pagina.
            </p>
            <p class="error-instructions">
              Tente limpar o cache do navegador e recarregar a pagina.
            </p>
            <v-btn
              color="primary"
              size="large"
              class="error-action"
              :loading="clearingCache"
              @click="clearClientCache"
            >
              Limpar cache
            </v-btn>
            <p class="error-contact">
              Se limpar o cache nao resolver, entre em contato com nossa equipe.
            </p>
          </template>
          <template v-else>
            <div class="error-status">{{ statusCode }}</div>
            <h1 class="error-title">Pagina indisponivel</h1>
            <p class="error-description">
              Nao foi possivel carregar a pagina solicitada.
            </p>
            <v-btn
              color="primary"
              size="large"
              class="error-action"
              @click="goHome"
            >
              Voltar para inicio
            </v-btn>
          </template>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>

<style scoped lang="scss">
.error-page {
  min-height: 100vh;
  background: linear-gradient(135deg, $gray-b1 0%, #ffffff 70%);
  display: flex;
}

.error-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-xl;
}

.error-card {
  background: #ffffff;
  border-radius: $border-radius-default;
  padding: $spacing-xl;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 16px 40px rgba(13, 48, 153, 0.12);
  text-align: center;
}

.error-status {
  font-size: 3.5rem;
  font-weight: 700;
  color: $blue-b1;
  margin-bottom: $spacing-sm;
}

.error-title {
  font-size: 1.8rem;
  margin-bottom: $spacing-sm;
  color: $gray-b4;
}

.error-description {
  font-size: 1rem;
  margin-bottom: $spacing-md;
  color: $gray-b3;
}

.error-instructions {
  font-size: 0.95rem;
  margin-bottom: $spacing-lg;
  color: $gray-b4;
}

.error-action {
  width: 100%;
  margin-bottom: $spacing-md;
}

.error-contact {
  font-size: 0.9rem;
  color: $gray-b3;
}

@media (max-width: 600px) {
  .error-card {
    padding: $spacing-lg;
  }

  .error-status {
    font-size: 3rem;
  }
}
</style>
